import * as React from 'react';
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import StyledCheckbox from './checkbox';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import TertiaryButton from './tertiary-button';
import { blue, neutral } from '../../theme/colors';

interface Props<OptionType> {
  idField: string;
  labelField: string;
  left: OptionType[];
  right: OptionType[];
  setLeft: React.Dispatch<React.SetStateAction<OptionType[]>>;
  setRight: React.Dispatch<React.SetStateAction<OptionType[]>>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void; //Formik function to set the field value
  setField: string; //The field param used in the setFieldValue function call
  leftLabel?: string;
  rightLabel?: string;
  height?: number | string;
  // rightListComponent?: (props: TransferListItemProps) => React.ReactElement;
  rightListComponent?: React.ComponentType<TransferListItemProps<OptionType>>;
  /** function to call on moving items from right to left (selected to unselected) */
  onMoveLeft?: (unselected: OptionType[]) => void;
  /** function to call on moving items from left to right (unselected to selected) */
  onMoveRight?: (selected: OptionType[]) => void;
}

const useStyles = makeStyles({
  wrapper: {
    margin: '16px 0'
  },
  root: {
    margin: 'auto',
    flexWrap: 'nowrap',
    alignItems: 'stretch'
  },
  paperContainer: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column'
  },
  paper: {
    flex: 1,
    minWidth: 200,
    width: '100%',
    overflow: 'auto',
    border: `1px solid ${neutral[300]}`,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column'
  },
  list: {
    flex: 1,
    paddingBottom: '24px'
  },
  buttonContainer: {
    padding: '16px'
  },
  listItem: {
    '& .MuiListItemIcon-root': {
      minWidth: '32px'
    }
  },
  selectAll: {
    color: blue[600],
    textTransform: 'uppercase',
    position: 'sticky',
    bottom: 0,
    boxSizing: 'border-box',
    width: '100%',
    margin: 0,
    padding: '8px 16px',
    backgroundColor: 'white'
  },
  sectionHeading: {
    fontSize: 11
  }
});

function not<T>(a: T[], b: T[]) {
  return a.filter(value => b.indexOf(value) === -1);
}

function intersection<T>(a: T[], b: T[]) {
  return a.filter(value => b.indexOf(value) !== -1);
}

/**
 * Default transfer list item
 * Can be overridden by the `rightListComponent` prop for extra functionality, ex. in the UserManager component.
 */
interface TransferListItemProps<OptionType> {
  value: OptionType;
  checked: OptionType[];
  idField: string;
  labelField: string;
  handleToggle: (value: object) => void;
}
const DefaultListItem = <T extends object>({
  value,
  checked,
  idField,
  labelField,
  handleToggle
}: TransferListItemProps<T>) => {
  const classes = useStyles();
  const labelId = `transfer-list-item-${value[idField]}-label`;

  return (
    <ListItem role="listitem" button onClick={() => handleToggle(value)} className={classes.listItem}>
      <ListItemIcon>
        <StyledCheckbox
          checked={checked.indexOf(value) !== -1}
          tabIndex={-1}
          disableRipple
          inputProps={{ 'aria-labelledby': labelId }}
        />
      </ListItemIcon>
      <ListItemText id={labelId} primary={value[labelField]} />
    </ListItem>
  );
};

/**
 * @function InnerTransferList This is a transfer list that lets you moves objects between two groups.
 */
const TransferList = <T extends object>({
  idField,
  labelField,
  left,
  setLeft,
  leftLabel,
  right,
  setRight,
  rightLabel,
  setFieldValue,
  setField,
  height,
  rightListComponent: RightListComponent = DefaultListItem,
  onMoveLeft,
  onMoveRight
}: Props<T>) => {
  const classes = useStyles(useTheme<Theme>());
  const [checked, setChecked] = React.useState<T[]>([]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: T) => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleCheckedRight = () => {
    onMoveRight && onMoveRight(leftChecked);
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
    setFieldValue(setField, right.concat(leftChecked));
  };

  const handleCheckedLeft = () => {
    onMoveLeft && onMoveLeft(rightChecked);
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
    setFieldValue(setField, not(right, rightChecked));
  };

  const selectAll = (items: T[]) => {
    setChecked([...checked, ...not(items, checked)]);
  };

  return (
    <div className={classes.wrapper}>
      <Grid
        container
        spacing={0}
        justifyContent="center"
        alignItems="center"
        className={classes.root}
        style={{ height: height ? height : 230 }}
      >
        <Grid item xs={6} className={classes.paperContainer}>
          <h6 className={classes.sectionHeading}>{leftLabel ? leftLabel : 'Available'}</h6>
          <Paper className={classes.paper}>
            <List dense role="list" className={classes.list}>
              {left.map((value: T) => (
                <DefaultListItem
                  key={value[idField]}
                  value={value}
                  checked={checked}
                  idField={idField}
                  labelField={labelField}
                  handleToggle={handleToggle}
                />
              ))}
            </List>
            <p className={classes.selectAll}>
              {left.length > 0 && (
                <TertiaryButton text="Select All" title="Select All" handleClick={() => selectAll(left)} />
              )}
            </p>
          </Paper>
        </Grid>
        <Grid item className={classes.buttonContainer}>
          <Grid container direction="column" alignItems="center">
            <IconButton
              size="small"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              <ArrowRightIcon fontSize="large" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              <ArrowLeftIcon fontSize="large" />
            </IconButton>
          </Grid>
        </Grid>
        <Grid item xs={6} className={classes.paperContainer}>
          <h6 className={classes.sectionHeading}>{rightLabel ? rightLabel : 'Selected'}</h6>
          <Paper className={classes.paper}>
            <List dense role="list" className={classes.list}>
              {right.map((value: T) =>
                RightListComponent ? (
                  <RightListComponent
                    key={value[idField]}
                    value={value}
                    checked={checked}
                    idField={idField}
                    labelField={labelField}
                    handleToggle={handleToggle}
                  />
                ) : (
                  <DefaultListItem
                    key={value[idField]}
                    value={value}
                    checked={checked}
                    idField={idField}
                    labelField={labelField}
                    handleToggle={handleToggle}
                  />
                )
              )}
            </List>
            <p className={classes.selectAll}>
              {right.length > 0 && (
                <TertiaryButton text="Select All" title="Select All" handleClick={() => selectAll(right)} />
              )}
            </p>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default TransferList;
